import { DEFAULT_ORG_SETTINGS } from "./organizationSettings";
import { loadCachedOrgSettings } from "./orgSettingsBridge";
import { buildOrganizationRegistrationLines } from "./organizationRegistration";

/**
 * Normalized branding snapshot for customer-facing reports (HTML + PDF).
 * Single source of truth — derived from organization settings only.
 *
 * @typedef {Object} OrgBrandingSnapshot
 * @property {string} brandName
 * @property {string|null} legalNameLine
 * @property {string|null} logoUrl
 * @property {string[]} contactLines
 * @property {string[]} registrationLines
 * @property {string} footerText
 * @property {string} pdfAuthor
 * @property {string|null} websiteSubtitle
 */

/**
 * @param {import("./organizationSettings").DEFAULT_ORG_SETTINGS | object|null|undefined} settings
 * @returns {OrgBrandingSnapshot}
 */
export function resolveOrgBranding(settings) {
  const source =
    settings && typeof settings === "object" ? settings : DEFAULT_ORG_SETTINGS;
  const org = source.organization || {};
  const branding = source.branding || {};

  const name = String(org.name || "").trim();
  const legalName = String(org.legal_name || "").trim();
  const brandName = name || legalName || "Organization";
  // Show legal name under the trading name when both exist and differ.
  const legalNameLine =
    name && legalName && legalName !== name ? legalName : null;

  const logoUrl =
    typeof branding.logo_url === "string" && branding.logo_url.trim()
      ? branding.logo_url.trim()
      : null;

  const contactLines = [];
  if (String(org.address || "").trim()) {
    contactLines.push(String(org.address).trim());
  }
  if (String(org.phone || "").trim()) {
    contactLines.push(String(org.phone).trim());
  }
  if (String(org.email || "").trim()) {
    contactLines.push(String(org.email).trim());
  }
  if (String(org.website || "").trim()) {
    contactLines.push(String(org.website).trim());
  }

  const website = String(org.website || "").trim() || null;

  return {
    brandName,
    legalNameLine,
    logoUrl,
    contactLines,
    registrationLines: buildOrganizationRegistrationLines(org),
    footerText: brandName,
    pdfAuthor: brandName,
    websiteSubtitle: website,
  };
}

/**
 * Resolve branding from offline cache when React context is unavailable (PDF export).
 * @param {string|null|undefined} organizationId
 * @returns {OrgBrandingSnapshot}
 */
export function resolveOrgBrandingFromCache(organizationId = null) {
  return resolveOrgBranding(loadCachedOrgSettings(organizationId));
}
